import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { X, Plus, Check } from 'lucide-react';
import { Button } from '../../../../../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../../components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '../../../../../components/ui/tooltip';
import { FilterValueInput } from './FilterValueInput';
import { FilterValueListInput } from './FilterValueListInput';
import { FkLookupValueButton } from './FkLookupValueButton';
import { JsonFilterFields } from './JsonFilterFields';
import { DeclareJsonColumnDialog } from './DeclareJsonColumnDialog';
import { EnumValuePicker } from './EnumValuePicker';
import { useEnumPickerSlot } from './useEnumPickerSlot';
import { useDeclareJsonSample } from './useDeclareJsonSample';
import { SpatialFilterFields } from './SpatialFilterFields';
import type {
  FilterGroup,
  FilterCondition,
  SpatialFilterContext,
  TableExplorerColumn,
} from '../../../core/types/workspaceTypes';
import type { FilterOperator, DataTypeCategory, JsonOperatorContext } from '../../utils/filterUtils';
import {
  excludeLookupWithoutBackingTable,
  getOperatorsForColumnWithMetaInfo,
  getOperatorLabel,
  inferSpatialFilterContext,
  operatorRequiresValue,
  operatorRequiresSecondValue,
  operatorIsArrayLength,
  operatorIsArrayElement,
  arrayElementDataType,
  categorizeDataType,
  formatFilterValue,
  hasFilterValue,
  isDeclareOnTextSupported,
  MIN_PG_MAJOR_VERSION_FOR_DECLARED_JSON,
  operatorTakesValueList,
  valueForOperator,
} from '../../utils/filterUtils';
import {
  categorizeJsonColumn,
  isNativeJsonColumn,
  type JsonColumnInput,
} from '../../utils/jsonColumnCategorization';
import { addJsonColumn } from '../../services/metaInfoApi';
import { useInvalidateMetaInfo } from '../../hooks/useMetaInfo';
import { getEngineLabel } from '../../utils/engineLabel';
import { useAnalytics } from '../../../../../core/analytics';
import { normalizeColumnName } from '../../utils/normalizeColumnName';
import { useMetaInfo } from '../../hooks/useMetaInfo';
import { escapeClaimProps } from '../../utils/escapeClaimTarget';
import type { TableExplorerCellValueCodec } from '../../../table-explorer/components/tableExplorerHostPorts';
import { hasRealForeignKeyTarget, looseTargetFilterForColumn } from '../../utils/looseFkResolver';

/**
 * Resolve the loose FK whose source AND target match the given column, so the
 * lookup picker on this filter can narrow target rows by the FK's `targetFilter`.
 * Matching by both endpoints picks the right declaration when one column has
 * multiple loose FKs (polymorphic associations).
 *
 * Only a LOOSE FK column has one: when the column's target is a real FK
 * constraint (`hasRealForeignKeyTarget`), the constraint wins over every
 * declaration on the column — including one that repeats the constraint's
 * endpoints exactly — so no declaration's `targetFilter` narrows the lookup.
 */
function useColumnTargetFilter(
  connectionId: string | undefined,
  database: string | undefined,
  tableSchema: string | undefined,
  tableName: string | undefined,
  fromColumn: string,
  column: TableExplorerColumn | undefined,
): FilterGroup[] | undefined {
  const { data: metaInfo } = useMetaInfo(connectionId ?? null, database ?? null);
  return useMemo(
    () => looseTargetFilterForColumn(metaInfo?.looseRelationships, tableSchema, tableName, fromColumn, column),
    [metaInfo, tableSchema, tableName, fromColumn, column],
  );
}

const friendlyDeclareError = (err: unknown): string => {
  if (err instanceof Error && err.message) return err.message;
  return 'Failed to declare the column as JSON. Check your connection and try again.';
};

/**
 * Build the unsupported-version message shown in the DeclareJsonColumnDialog
 * when the user tries to declare on a PG <16 server. Returns `null` when the
 * combination is supported (engine other than PG, or PG 16+, or unknown
 * version) — `null` lets the dialog operate normally.
 */
function declareOnTextUnsupportedReason(
  jsonContext: JsonOperatorContext | undefined,
): string | null {
  if (!jsonContext) return null;
  if (isDeclareOnTextSupported(jsonContext)) return null;
  // PG <16 is the only failure path the predicate produces today.
  const v = jsonContext.pgMajorVersion;
  const detected = typeof v === 'number' ? `Detected PostgreSQL ${v}.` : '';
  return `Declaring text columns as JSON requires PostgreSQL ${MIN_PG_MAJOR_VERSION_FOR_DECLARED_JSON}+ (uses the IS JSON predicate). ${detected} Use a native jsonb column for JSON filtering on this server.`.trim();
}

/**
 * Resolve the loose-FK `sourcePath` for `(schema, table, column)` from
 * MetaInfo, if one exists. Slice 16 — when set, the `lookup` operator emits
 * a JSON-extract against this path instead of comparing the source column
 * directly. Returns `undefined` for non-JSON loose FKs and when no relation
 * matches, so callers can spread it conditionally.
 */
function findLooseSourcePath(
  jsonContext: JsonOperatorContext | undefined,
  sourceColumn: string,
): string | undefined {
  if (!jsonContext?.metaInfo) return undefined;
  const rel = jsonContext.metaInfo.looseRelationships.find(
    (r) =>
      r.fromSchema === jsonContext.schema &&
      r.fromTable === jsonContext.table &&
      r.fromColumn === sourceColumn,
  );
  return rel?.sourcePath || undefined;
}

const newDeclarationId = (): string =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `json-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

/**
 * Compact, read-only summary for an enum multi-select array value (mirrors
 * `FilterChipCompact`'s wording). Rendered in place of the editable text
 * control whenever an `enum` condition holds an array but the EnumValuePicker
 * is unavailable — text-editing an array would silently corrupt it to the
 * scalar `String(['A','B'])` === "A,B".
 */
const enumArraySummary = (value: readonly unknown[]): string =>
  `in ${value.length} ${value.length === 1 ? 'value' : 'values'}`;

/**
 * A list value as the list entry takes it (anything else is an empty list). A
 * `bigint` member (the condition type allows it for host codecs; the list entry
 * never produces one) becomes its exact digits, as the entry keeps a number past
 * 2^53.
 */
const listValueOf = (value: FilterCondition['value'] | undefined): (string | number)[] =>
  Array.isArray(value) ? value.map((v) => (typeof v === 'bigint' ? v.toString() : v)) : [];

/**
 * `feature:in_list_filter_apply` for every "in list" / "not in list" condition
 * being applied — the operator and how many values, nothing else.
 */
function trackListFilters(
  track: ReturnType<typeof useAnalytics>['track'],
  engine: string | undefined,
  conditions: readonly FilterCondition[],
): void {
  for (const c of conditions) {
    if (!operatorTakesValueList(c.operator) || !hasFilterValue(c.value)) continue;
    track('feature:in_list_filter_apply', {
      engine: engine ?? 'unknown',
      operator: c.operator === 'IN' ? 'in' : 'not_in',
      count: listValueOf(c.value).length,
    });
  }
}

type FilterChipProps = {
  filterGroup: FilterGroup;
  column: TableExplorerColumn | undefined;
  onUpdate: (updatedGroup: FilterGroup) => void;
  onRemove: () => void;
  /** Connection id — required for the FK lookup picker (operator='lookup'). */
  connectionId?: string;
  /** Database name — required for the FK lookup picker. */
  database?: string;
  /** Source schema for loose-FK target picker context (only relevant when filtering on non-FK columns with the lookup operator). */
  tableSchema?: string;
  /** Source table for loose-FK target picker context. */
  tableName?: string;
  /** Engine + metaInfo context for the `json` operator (placement + declare-on-text confirmation). */
  jsonContext?: JsonOperatorContext;
  /**
   * Currently loaded grid rows. Used solely to source a single non-null
   * sample value for the Declare-as-JSON confirmation dialog, so the user
   * can eyeball "this really is JSON" before declaring. Never sent over IPC.
   */
  sampleRows?: ReadonlyArray<Record<string, unknown>>;
  /**
   * Fires whenever the chip's staged (uncommitted) edit state changes —
   * payload is the effective FilterGroup with staged value / json-draft
   * overrides applied, or `null` when the chip matches its committed state.
   * Used by the FilterWizard's *Peek matching rows* button so the preview
   * reflects edits in progress, not just the last Apply.
   */
  onDirtyChange?: (dirty: FilterGroup | null) => void;
  /**
   * Applied Table Explorer filters (minus this chip's own group) to scope
   * the JSON Peek popover's scan to — filter-aware JSON peek (Path A).
   * Passed down by FilterWizard via `scanFiltersForChip`.
   */
  scanFilters?: FilterGroup[];
  valueCodec?: TableExplorerCellValueCodec;
  /**
   * Host value codecs only (Azure Table, S4 review D, D3): the tag of ONE operand — `value` or `value2` of this
   * condition (`undefined` = a new condition) — so each input parses with its own operand's type.
   */
  valueTagFor?: (condition: FilterCondition | undefined, operand: 'value' | 'value2') => string | undefined;
};

type PendingJsonDeclaration = {
  conditionIndex: number;
} | null;

/** A value being edited: one value, or the list of an "in list" condition. */
type DraftValue = string | number | bigint | boolean | Date | null | undefined | (string | number | bigint)[];

// Track which value is being edited and the draft value
type EditingState = {
  conditionIndex: number;
  valueType: 'primary' | 'secondary';
  draftValue: DraftValue;
} | null;

// Draft OR condition being added (not yet committed)
type DraftOrCondition = {
  operator: string;
  value: DraftValue;
  value2?: string | number | bigint | Date | undefined;
} | null;

/**
 * FilterChip for committed (applied) filters
 */
export function FilterChip({
  filterGroup,
  column,
  onUpdate,
  onRemove,
  connectionId,
  database,
  tableSchema,
  tableName,
  jsonContext,
  sampleRows,
  onDirtyChange,
  scanFilters,
  valueCodec,
  valueTagFor,
}: FilterChipProps) {
  const [editing, setEditing] = useState<EditingState>(null);
  const [draftOrCondition, setDraftOrCondition] = useState<DraftOrCondition>(null);
  const [pendingDeclaration, setPendingDeclaration] = useState<PendingJsonDeclaration>(null);
  const [declarationError, setDeclarationError] = useState<string | null>(null);
  const invalidateMetaInfo = useInvalidateMetaInfo();
  const declareSample = useDeclareJsonSample({
    rows: sampleRows,
    column: column?.name,
    connectionId,
    database,
    schema: jsonContext?.schema,
    table: jsonContext?.table,
    engine: jsonContext?.engine,
    filters: scanFilters,
    open: !!pendingDeclaration,
  });
  // Per-condition staged JSON-operator edits. JsonFilterFields fires
  // `onChange` on every keystroke / sub-op change / path pick — without
  // staging, those would commit immediately and re-run the SQL on every
  // change. We mirror the value-edit flow: stage in the draft, surface a
  // ✓/✗ Apply pair, commit via `onUpdate` only on confirm.
  const [jsonDrafts, setJsonDrafts] = useState<Map<number, FilterCondition>>(
    () => new Map(),
  );
  const inputRef = useRef<HTMLInputElement>(null);
  const draftInputRef = useRef<HTMLInputElement>(null);
  // Per-condition refs for the JSON Apply (✓) button — used as the
  // post-pick focus target from JsonFilterFields/JsonPeekPopover so Enter
  // can commit the draft without reaching for the mouse.
  const jsonApplyRefs = useRef<
    Map<number, { current: HTMLButtonElement | null }>
  >(new Map());
  const getJsonApplyRef = (
    index: number,
  ): { current: HTMLButtonElement | null } => {
    let ref = jsonApplyRefs.current.get(index);
    if (!ref) {
      ref = { current: null };
      jsonApplyRefs.current.set(index, ref);
    }
    return ref;
  };
  // Track which field we're editing to only focus on initial open
  const editingKeyRef = useRef<string | null>(null);
  const { track } = useAnalytics();

  const dataTypeCategory: DataTypeCategory = column
    ? categorizeDataType(column.dataType)
    : 'unknown';
  const availableOperators = getOperatorsForColumnWithMetaInfo(column, jsonContext);
  const displayFilterValue = (value: unknown, tag?: string): string => valueCodec && value != null
    ? valueCodec.format(value, tag)
    : formatFilterValue(value);
  // A host codec input reports an invalid draft (D3); an invalid draft is never committed.
  const [codecEditError, setCodecEditError] = useState<string>();
  const [codecDraftOrError, setCodecDraftOrError] = useState<string>();
  // The picker itself handles the "no declared target yet" case (it shows the
  // loose-FK target selection UI) so all we need here is a connection +
  // database. FK columns and non-FK columns alike route through the picker.
  const canRenderLookupPicker = !!connectionId && !!database;

  // Per-condition value-input category: array_length_* always takes an integer,
  // and array_contains / array_not_contains take an element of the array's
  // element type (strip trailing `[]`). Other operators use the column's own
  // category.
  const valueCategoryFor = (operator: FilterOperator): DataTypeCategory => {
    if (operatorIsArrayLength(operator)) return 'number';
    if (operatorIsArrayElement(operator) && column?.dataType) {
      return categorizeDataType(arrayElementDataType(column.dataType));
    }
    return dataTypeCategory;
  };

  // `array_contains` doubles as the FK-picker entry point when the column is
  // a (real or loose) FK — same UX as `lookup` for scalar columns.
  // Only `lookup` uses the FK picker. `array_contains` (and other array ops)
  // use the regular value-input UX — free-text or category-typed input. The
  // user can switch to `lookup` from the dropdown to get the picker version,
  // which the provider lowers to `array_contains` SQL semantics on array fields.
  const operatorUsesFkPicker = (operator: FilterOperator): boolean => {
    if (!canRenderLookupPicker) return false;
    return operator === 'lookup';
  };

  const targetFilter = useColumnTargetFilter(
    connectionId,
    database,
    tableSchema,
    tableName,
    filterGroup.field,
    column,
  );

  // EnumValuePicker — rendered when the `enum` operator is selected.
  const { data: metaInfoForEnum } = useMetaInfo(
    connectionId ?? null,
    database ?? null,
  );
  const enumSlot = useEnumPickerSlot({
    connectionId,
    database,
    schema: tableSchema,
    table: tableName,
    engine: jsonContext?.engine,
    column,
    metaInfo: metaInfoForEnum,
  });

  // Focus input only when first entering edit mode (not on every draftValue change)
  const currentEditingKey = editing ? `${editing.conditionIndex}-${editing.valueType}` : null;
  useEffect(() => {
    if (currentEditingKey && currentEditingKey !== editingKeyRef.current) {
      editingKeyRef.current = currentEditingKey;
      const openedWith = inputRef.current?.value;
      const timeout = setTimeout(() => {
        // Typing already began: selecting it now would let the next key replace it.
        if (inputRef.current && inputRef.current.value !== openedWith) return;
        inputRef.current?.focus();
        inputRef.current?.select();
      }, 50);
      return () => clearTimeout(timeout);
    }
    if (!currentEditingKey) {
      editingKeyRef.current = null;
    }
  }, [currentEditingKey]);

  // Focus draft OR input when it appears
  useEffect(() => {
    if (draftOrCondition) {
      const timeout = setTimeout(() => {
        draftInputRef.current?.focus();
      }, 50);
      return () => clearTimeout(timeout);
    }
  }, [draftOrCondition !== null]); // eslint-disable-line react-hooks/exhaustive-deps

  // Update a specific condition (only called on confirm)
  const handleConditionChange = useCallback(
    (index: number, updates: Partial<FilterCondition>) => {
      const newConditions = [...filterGroup.conditions];
      newConditions[index] = { ...newConditions[index], ...updates };
      onUpdate({ ...filterGroup, conditions: newConditions });
    },
    [filterGroup, onUpdate]
  );

  // Stage a partial change to a `json`-operator condition. Reads the
  // existing draft (if any) so successive edits accumulate; otherwise
  // seeds the draft from the committed condition.
  const handleJsonDraftChange = useCallback(
    (index: number, updates: Partial<FilterCondition>) => {
      setJsonDrafts((prev) => {
        const base = prev.get(index) ?? filterGroup.conditions[index];
        const next = new Map(prev);
        next.set(index, { ...base, ...updates });
        return next;
      });
    },
    [filterGroup.conditions],
  );

  const handleJsonApply = useCallback(
    (index: number) => {
      const draft = jsonDrafts.get(index);
      if (!draft) return;
      const newConditions = [...filterGroup.conditions];
      newConditions[index] = draft;
      onUpdate({ ...filterGroup, conditions: newConditions });
      setJsonDrafts((prev) => {
        const next = new Map(prev);
        next.delete(index);
        return next;
      });
    },
    [filterGroup, jsonDrafts, onUpdate],
  );

  const handleJsonCancel = useCallback((index: number) => {
    setJsonDrafts((prev) => {
      if (!prev.has(index)) return prev;
      const next = new Map(prev);
      next.delete(index);
      return next;
    });
  }, []);

  const handleJsonKeyDown = useCallback(
    (index: number, e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        // Stop propagation once this cluster has fully handled the key —
        // otherwise the root's `handleChipRootKeyDown` also sees the (still
        // bubbling) event on a stale-closure `jsonDrafts`, see fix round 3
        // Finding 1.
        e.stopPropagation();
        handleJsonApply(index);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        handleJsonCancel(index);
      }
    },
    [handleJsonApply, handleJsonCancel],
  );

  // Annotate a `json`-operator condition with `jsonDeclaredOnText` flag
  // for engines that need a JSON-validity guard wrap. Native JSON columns
  // skip the wrap; declared-on-text columns get it.
  const annotateJsonCondition = useCallback(
    (base: Partial<FilterCondition>): Partial<FilterCondition> => {
      if (base.operator !== 'json') return base;
      if (!column || !jsonContext) return { ...base, jsonSubOperator: base.jsonSubOperator ?? 'has_property' };
      const input: JsonColumnInput = {
        engine: jsonContext.engine,
        schema: jsonContext.schema,
        table: jsonContext.table,
        name: column.name,
        dataType: column.dataType,
      };
      const cat = categorizeJsonColumn(input, jsonContext.metaInfo);
      return {
        ...base,
        jsonSubOperator: base.jsonSubOperator ?? 'has_property',
        jsonDeclaredOnText: cat === 'declared-json' && !isNativeJsonColumn(input),
      };
    },
    [column, jsonContext]
  );

  // Operator-change handler aware of the `json` declaration flow.
  // Picking `json` on a `json-eligible` (long-text, undeclared) column
  // opens the declaration confirmation modal; the operator is applied
  // only on confirm. All other operator changes pass through.
  const handleOperatorPick = useCallback(
    (index: number, value: FilterOperator) => {
      if (value === 'json' && column && jsonContext) {
        const input: JsonColumnInput = {
          engine: jsonContext.engine,
          schema: jsonContext.schema,
          table: jsonContext.table,
          name: column.name,
          dataType: column.dataType,
        };
        const cat = categorizeJsonColumn(input, jsonContext.metaInfo);
        if (cat === 'json-eligible') {
          setPendingDeclaration({ conditionIndex: index });
          return;
        }
      }
      // Operator change is a decisive commit — discard any in-flight JSON
      // draft for this index so the new operator's defaults take effect.
      if (jsonDrafts.has(index)) {
        setJsonDrafts((prev) => {
          const next = new Map(prev);
          next.delete(index);
          return next;
        });
      }
      handleConditionChange(
        index,
        annotateJsonCondition({
          operator: value,
          // A list operator starts from an empty list (shown as "empty").
          value: operatorTakesValueList(value) ? [] : undefined,
          value2: undefined,
          // Clear lookup-only sourcePath when leaving the `lookup` operator
          // so the next operator's emit doesn't see stale JSON-extract state.
          sourcePath: undefined,
        }),
      );
    },
    [annotateJsonCondition, column, handleConditionChange, jsonContext, jsonDrafts]
  );

  const handleConfirmDeclaration = useCallback(async () => {
    if (!pendingDeclaration || !column || !jsonContext || !connectionId || !database) {
      setPendingDeclaration(null);
      setDeclarationError(null);
      return;
    }
    const idx = pendingDeclaration.conditionIndex;
    setDeclarationError(null);
    try {
      await addJsonColumn(connectionId, database, {
        id: newDeclarationId(),
        schema: jsonContext.schema,
        table: jsonContext.table,
        column: column.name,
        createdAt: new Date().toISOString(),
      });
      track('feature:json_declare', {
        engine: jsonContext.engine,
        source: 'filter-confirm',
      });
      // Refresh the cached MetaInfo so downstream consumers (Manage Meta-Info
      // dialog, future categorize() calls) see the new declaration without
      // waiting for staleTime to elapse.
      await invalidateMetaInfo(connectionId, database);
    } catch (err) {
      // Surface the failure inline so the user can retry without re-opening
      // the dropdown — leaves the dialog open with the error banner.
      setDeclarationError(friendlyDeclareError(err));
      return;
    }
    handleConditionChange(idx, {
      operator: 'json',
      jsonSubOperator: 'has_property',
      jsonDeclaredOnText: true,
      value: undefined,
      value2: undefined,
    });
    setPendingDeclaration(null);
  }, [column, connectionId, database, handleConditionChange, invalidateMetaInfo, jsonContext, pendingDeclaration, track]);

  const handleCancelDeclaration = useCallback(() => {
    setPendingDeclaration(null);
    setDeclarationError(null);
  }, []);

  // Start adding a new OR condition (creates local draft, doesn't update parent)
  const handleAddOrCondition = useCallback(() => {
    const defaultOperator = availableOperators[0] || '=';
    setDraftOrCondition({
      operator: defaultOperator,
      value: '',
    });
  }, [availableOperators]);

  // Confirm the draft OR condition - add to parent
  const handleConfirmDraftOr = useCallback(() => {
    if (codecDraftOrError) return;
    if (draftOrCondition) {
      const newCondition: FilterCondition = {
        operator: draftOrCondition.operator as FilterCondition['operator'],
        value: draftOrCondition.value,
        value2: draftOrCondition.value2,
      };
      if (newCondition.operator === 'json' && jsonContext) {
        track('feature:json_filter_apply', {
          engine: jsonContext.engine,
          sub_operator: newCondition.jsonSubOperator ?? 'has_property',
          has_path: !!newCondition.path,
        });
      }
      trackListFilters(track, jsonContext?.engine, [newCondition]);
      onUpdate({
        ...filterGroup,
        conditions: [...filterGroup.conditions, newCondition],
      });
      setDraftOrCondition(null);
    }
  }, [codecDraftOrError, draftOrCondition, filterGroup, jsonContext, onUpdate, track]);

  // Cancel the draft OR condition
  const handleCancelDraftOr = useCallback(() => {
    setDraftOrCondition(null);
  }, []);

  // Update draft OR condition value
  const handleDraftOrValueChange = useCallback(
    (value: DraftValue) => {
      if (draftOrCondition) {
        setDraftOrCondition({ ...draftOrCondition, value });
      }
    },
    [draftOrCondition]
  );

  // Update draft OR condition operator
  const handleDraftOrOperatorChange = useCallback(
    (operator: FilterOperator) => {
      if (draftOrCondition) {
        // A value typed for one operator carries over — as a one-value list
        // into "in list", as a list's first value out of it.
        setDraftOrCondition({ ...draftOrCondition, operator, value: valueForOperator(draftOrCondition.value, operator) });
        // Auto-confirm for operators that don't require a value (but NOT for
        // `json`, which still needs a sub-op + path before commit; otherwise
        // we'd commit a no-op `has_property '$'` filter that returns all rows).
        if (!operatorRequiresValue(operator) && operator !== 'json') {
          const newCondition: FilterCondition = {
            operator,
            value: undefined,
          };
          onUpdate({
            ...filterGroup,
            conditions: [...filterGroup.conditions, newCondition],
          });
          setDraftOrCondition(null);
        }
      }
    },
    [draftOrCondition, filterGroup, onUpdate]
  );

  // Handle keyboard in draft OR mode
  const handleDraftOrKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        // Belt-and-braces alongside `handleJsonKeyDown`'s fix (round 3,
        // Finding 1) — this handler is currently idempotent with the root's
        // fallback (both cancel the SAME draft OR), but stop propagation
        // anyway so correctness doesn't rely on that incidental idempotence.
        e.stopPropagation();
        handleConfirmDraftOr();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        handleCancelDraftOr();
      }
    },
    [handleConfirmDraftOr, handleCancelDraftOr]
  );

  // Remove a condition
  const handleRemoveCondition = useCallback(
    (index: number) => {
      // Drop any staged JSON drafts — indices shift after the splice, so
      // remapping per-index drafts is fragile; clearing is safer than
      // letting a draft point at the wrong condition.
      if (jsonDrafts.size > 0) setJsonDrafts(new Map());
      if (filterGroup.conditions.length === 1) {
        // If this is the last condition, remove the entire filter group
        onRemove();
      } else {
        const newConditions = filterGroup.conditions.filter((_, i) => i !== index);
        onUpdate({ ...filterGroup, conditions: newConditions });
      }
    },
    [filterGroup, jsonDrafts.size, onUpdate, onRemove]
  );

  // Start editing a value - store current value as draft
  const handleStartEdit = useCallback(
    (conditionIndex: number, valueType: 'primary' | 'secondary') => {
      const condition = filterGroup.conditions[conditionIndex];
      const target =
        valueType === 'secondary' ? condition.value2 : condition.value;
      // An "in list" value is edited as a list (FilterValueListInput).
      if (operatorTakesValueList(condition.operator)) {
        setEditing({ conditionIndex, valueType, draftValue: listValueOf(condition.value) });
        return;
      }
      // Arrays (enum multi-select) must never enter the scalar text-edit draft
      // flow — `String(['A','B'])` would silently rewrite the filter to the
      // scalar "A,B". They go through EnumValuePicker instead; when the picker
      // is unavailable the array is rendered read-only (see Site 1 below).
      if (Array.isArray(target)) return;
      setEditing({ conditionIndex, valueType, draftValue: target });
    },
    [filterGroup.conditions]
  );

  // Update draft value (doesn't affect the actual filter)
  const handleDraftChange = useCallback(
    (value: DraftValue) => {
      if (editing) {
        setEditing({ ...editing, draftValue: value });
      }
    },
    [editing]
  );

  // Confirm edit - apply draft value to the filter
  const handleConfirmEdit = useCallback(() => {
    if (codecEditError) return;
    if (editing) {
      const { conditionIndex, valueType, draftValue } = editing;
      if (valueType === 'primary') {
        handleConditionChange(conditionIndex, { value: draftValue });
        const edited = filterGroup.conditions[conditionIndex];
        if (edited) trackListFilters(track, jsonContext?.engine, [{ ...edited, value: draftValue }]);
      } else {
        handleConditionChange(conditionIndex, { value2: draftValue as string | number | bigint | Date | undefined });
      }
      setEditing(null);
    }
  }, [codecEditError, editing, filterGroup.conditions, handleConditionChange, jsonContext?.engine, track]);

  // Cancel edit - discard draft value
  const handleCancelEdit = useCallback(() => {
    setEditing(null);
  }, []);

  // Handle keyboard in edit mode
  const handleEditKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        // Belt-and-braces alongside `handleJsonKeyDown`'s fix (round 3,
        // Finding 1) — currently idempotent with the root's fallback (both
        // cancel the SAME edit), but stop propagation anyway so correctness
        // doesn't rely on that incidental idempotence.
        e.stopPropagation();
        handleConfirmEdit();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        handleCancelEdit();
      }
    },
    [handleConfirmEdit, handleCancelEdit]
  );

  const isEditing =
    editing !== null || draftOrCondition !== null || jsonDrafts.size > 0;

  // codex P2 #2 — the root's `escapeClaimProps` marker (below) claims Escape
  // for this chip's WHOLE subtree while `isEditing`, so an ancestor Dialog
  // host correctly stops ITS OWN dismiss — but until this handler existed
  // that claim was never backed by an actual cancel unless the event
  // happened to originate on the value input (the only descendant with its
  // own Escape-handling `onKeyDown`). Tabbing to the operator select, the
  // JSON Apply/Cancel pair, or the primary/secondary Confirm/Cancel buttons
  // and pressing Escape there did nothing. Route Escape here too, keyed off
  // which edit is actually in progress (state, not event target — several of
  // these can't coexist for the SAME condition, and when more than one kind
  // is open at once cancelling all of them is the safe default).
  const handleChipRootKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key !== 'Escape' || !isEditing) return;
      e.preventDefault();
      e.stopPropagation();
      if (editing) {
        handleCancelEdit();
      } else if (draftOrCondition) {
        handleCancelDraftOr();
      } else if (jsonDrafts.size > 0) {
        // fix round 3, Finding 1 — this only runs for an Escape that a JSON
        // cluster's OWN handler (`handleJsonKeyDown`) didn't already catch
        // (that one stops propagation once it acts), e.g. Escape from a
        // condition's operator trigger, which sits outside the cluster's
        // `onKeyDown` span. Scope the cancel to the condition the event
        // actually originated on — via `data-condition-index`, stamped on
        // each condition's wrapper below — so this fallback can never wipe
        // a SIBLING condition's still-open JSON draft. Only clear every
        // draft when the originating condition can't be determined at all.
        const target = e.target as HTMLElement | null;
        const indexAttr = target
          ?.closest<HTMLElement>('[data-condition-index]')
          ?.dataset.conditionIndex;
        const index = indexAttr !== undefined ? Number(indexAttr) : NaN;
        if (!Number.isNaN(index) && jsonDrafts.has(index)) {
          handleJsonCancel(index);
        } else {
          setJsonDrafts(new Map());
        }
      }
    },
    [
      isEditing,
      editing,
      draftOrCondition,
      jsonDrafts,
      handleCancelEdit,
      handleCancelDraftOr,
      handleJsonCancel,
    ],
  );

  // Check if draft OR has a valid value (a list needs at least one value)
  const draftOrHasValidValue = draftOrCondition
    ? !operatorRequiresValue(draftOrCondition.operator as FilterOperator) || hasFilterValue(draftOrCondition.value)
    : false;

  // Merge staged-but-not-yet-committed edits into the committed group so
  // upstream consumers (FilterWizard's *Peek matching rows*) can preview the
  // SQL that *would* run if the user applied. Mirrors handleConfirmEdit /
  // handleApplyJsonDraft / draft-OR commit so the SQL matches.
  const effectiveDirtyGroup: FilterGroup | null = useMemo(() => {
    if (!isEditing) return null;
    const baseConditions = filterGroup.conditions.map((c, i) => {
      if (jsonDrafts.has(i)) return jsonDrafts.get(i) as FilterCondition;
      if (editing?.conditionIndex === i) {
        if (editing.valueType === 'primary') {
          return { ...c, value: editing.draftValue as FilterCondition['value'] };
        }
        return { ...c, value2: editing.draftValue as FilterCondition['value2'] };
      }
      return c;
    });
    const conditions: FilterCondition[] = [...baseConditions];
    if (draftOrCondition && draftOrHasValidValue) {
      conditions.push({
        operator: draftOrCondition.operator as FilterCondition['operator'],
        value: draftOrCondition.value,
        value2: draftOrCondition.value2,
      });
    }
    return { ...filterGroup, conditions };
  }, [
    isEditing,
    filterGroup,
    jsonDrafts,
    editing,
    draftOrCondition,
    draftOrHasValidValue,
  ]);

  useEffect(() => {
    onDirtyChange?.(effectiveDirtyGroup);
  }, [effectiveDirtyGroup, onDirtyChange]);

  // Clear dirty state on unmount so the wizard doesn't stick on a removed
  // chip's last staged value.
  useEffect(() => {
    return () => onDirtyChange?.(null);
  }, [onDirtyChange]);

  return (
    <div
      data-testid={`filter-chip-${normalizeColumnName(filterGroup.field)}`}
      className={`flex items-center gap-1 rounded-md px-2 py-1 ${
        isEditing
          ? 'border-2 border-blue-500 bg-blue-50 dark:bg-blue-950/30'
          : 'border border-border bg-muted/50'
      }`}
      // F1-#2 — Escape-in-popover seam consolidation. Claim Escape for this
      // chip's own in-place edits (primary/secondary value edit, JSON-draft,
      // OR-draft) ONLY while one is in progress (`isEditing`) — a Dialog
      // host (`FkLookupPicker`, `JsonStructureDetailsDialog`) checks for this
      // marker via `isEscapeClaimTarget` in its own `onEscapeKeyDown` so
      // Escape cancels the edit only, never dismisses the ancestor dialog;
      // inert (and harmless) everywhere else (Table Explorer's own non-Dialog
      // FilterWizard host has no such ancestor check).
      {...(isEditing ? escapeClaimProps : {})}
      onKeyDown={handleChipRootKeyDown}
    >
      {/* Field name */}
      <span translate="no" className="text-xs font-medium text-foreground shrink-0">
        {filterGroup.field}
      </span>

      {/* Conditions */}
      <div className="flex items-center gap-1 flex-wrap">
        {filterGroup.conditions.map((condition, index) => {
          const isEditingPrimary = editing?.conditionIndex === index && editing?.valueType === 'primary';
          const isEditingSecondary = editing?.conditionIndex === index && editing?.valueType === 'secondary';

          return (
            <div key={index} className="flex items-center gap-1" data-condition-index={index}>
              {/* OR separator */}
              {index > 0 && (
                <span className="text-xs text-muted-foreground px-1">OR</span>
              )}

              {/* Operator select */}
              <Select
                value={condition.operator}
                onValueChange={(value: FilterOperator) => handleOperatorPick(index, value)}
              >
                <SelectTrigger className="h-6 w-auto min-w-[70px] px-2 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent portalled={false}>
                  {availableOperators.map((op) => (
                    <SelectItem key={op} value={op} className="text-xs">
                      {getOperatorLabel(op)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* JSON operator field cluster — JsonFilterFields fires
                  `onChange` on every keystroke / sub-op change / path
                  pick. Stage those edits in `jsonDrafts` and commit only
                  on ✓ Apply (mirrors the value-edit flow above). */}
              {condition.operator === 'json' && (() => {
                const effectiveCondition =
                  jsonDrafts.get(index) ?? condition;
                const isJsonDirty = jsonDrafts.has(index);
                const applyRef = getJsonApplyRef(index);
                return (
                  <span
                    className="inline-flex flex-wrap items-center gap-1 max-w-md"
                    onKeyDown={(e) => handleJsonKeyDown(index, e)}
                  >
                    <JsonFilterFields
                      condition={effectiveCondition}
                      onChange={(updates) => handleJsonDraftChange(index, updates)}
                      engine={jsonContext?.engine}
                      applyButtonRef={applyRef}
                      peekContext={
                        column && jsonContext && connectionId && database
                          ? {
                              connectionId,
                              database,
                              schema: jsonContext.schema,
                              table: jsonContext.table,
                              column: column.name,
                              engine: jsonContext.engine,
                              declaredOnText: !!effectiveCondition.jsonDeclaredOnText,
                              dataType: column.dataType,
                              metaInfo: jsonContext.metaInfo ?? null,
                              scanFilters,
                            }
                          : undefined
                      }
                    />
                    {isJsonDirty && (
                      <>
                        <Button
                          ref={(el) => {
                            applyRef.current = el;
                          }}
                          variant="ghost"
                          size="sm"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => handleJsonApply(index)}
                          className="h-5 w-5 p-0 hover:bg-green-500/20 text-green-600"
                          title="Apply (Enter)"
                          aria-label="Apply JSON filter changes"
                          data-testid="json-filter-apply"
                        >
                          <Check className="h-3 w-3" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleJsonCancel(index)}
                          className="h-5 w-5 p-0 hover:bg-destructive/20"
                          title="Cancel (Escape)"
                          aria-label="Cancel JSON filter changes"
                          data-testid="json-filter-cancel"
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </>
                    )}
                  </span>
                );
              })()}

              {/* Spatial operator field cluster — sub-op select + read-only
                  bbox/point display + distance input. The bbox/point are
                  populated from the map (TE "Filter by map view" / map-click),
                  not typed here. Commits immediately via handleConditionChange
                  (no free-text-confirm dance; distance input self-debounces on
                  blur/Enter inside SpatialFilterFields). */}
              {condition.operator === 'spatial' && jsonContext && (() => {
                // T19 — before a bbox/point has been set (i.e. before "Filter by
                // map view" or a map click stamps `condition.spatialContext`),
                // seed the sub-op gating from the column's declared/native shape
                // so a declared lat/lng-pair column's "in map area" sub-op shows
                // correctly enabled everywhere, not just where a native geometry
                // column's dataType-regex guess happens to be right.
                const spatialCtx: SpatialFilterContext =
                  condition.spatialContext ?? inferSpatialFilterContext(column, jsonContext);
                return (
                  <SpatialFilterFields
                    condition={condition}
                    engine={jsonContext.engine}
                    spatialContext={spatialCtx}
                    onChange={(updates) => handleConditionChange(index, updates)}
                  />
                );
              })()}

              {/* Primary value - editable on click */}
              {operatorRequiresValue(condition.operator) && (
                operatorUsesFkPicker(condition.operator) ? (
                  <FkLookupValueButton
                    connectionId={connectionId!}
                    database={database!}
                    sourceColumn={filterGroup.field}
                    sourceSchema={tableSchema}
                    sourceTable={tableName}
                    sourceColumnDataType={column?.dataType ?? 'unknown'}
                    referencedSchema={column?.referencedSchema ?? ''}
                    referencedTable={column?.referencedTable ?? ''}
                    referencedColumn={column?.referencedColumn ?? ''}
                    referencesRealForeignKey={hasRealForeignKeyTarget(column)}
                    targetFilter={targetFilter}
                    value={condition.value as string | number | boolean | Date | null | undefined}
                    onChange={(v, resolvedSourcePath) => {
                      // Picker's resolved path is the user's actual pick
                      // (Pass 3 Slice A's deferred write-back) — prefer it
                      // over the find-first-match fallback so multi-FK
                      // columns disambiguate correctly.
                      const sourcePath =
                        resolvedSourcePath ??
                        condition.sourcePath ??
                        findLooseSourcePath(jsonContext, filterGroup.field);
                      handleConditionChange(index, { value: v, sourcePath });
                    }}
                    engine={jsonContext?.engine}
                    sourcePath={condition.sourcePath}
                    onClearFilter={onRemove}
                  />
                ) : condition.operator === 'enum' && enumSlot.available ? (
                  // The chip's enum *summary* is the picker's own trigger
                  // label: on a lookup-declared column it reads `<Label> ·<key>`
                  // (so the chip as a whole reads `status_id: Active ·3`), and
                  // `in N values` stays as-is for multiselect. The label comes
                  // from the cached pairs the picker already holds — no second
                  // MetaInfo read here. `enumSlot.context` also carries the
                  // resolved FK target that powers the picker's
                  // "Search rows in …" escape.
                  <EnumValuePicker
                    {...enumSlot.context}
                    operator={Array.isArray(condition.value) ? 'in' : 'eq'}
                    value={condition.value}
                    onChange={(next) => {
                      handleConditionChange(index, { value: next });
                      track('feature:enum_filter_apply', {
                        engine: enumSlot.context.engine,
                        operator: 'enum',
                        source: Array.isArray(next) ? 'multi' : 'pick',
                        ...(Array.isArray(next) ? { count: next.length } : {}),
                      });
                    }}
                    ariaLabel={`Pick value for ${filterGroup.field}`}
                  />
                ) : isEditingPrimary ? (
                  <>
                    {operatorTakesValueList(condition.operator) ? (
                      <FilterValueListInput
                        ref={inputRef}
                        values={listValueOf(editing.draftValue as FilterCondition['value'])}
                        onChange={handleDraftChange}
                        onKeyDown={handleEditKeyDown}
                        dataTypeCategory={valueCategoryFor(condition.operator)}
                        autoFocus
                      />
                    ) : (
                      <FilterValueInput
                        ref={inputRef}
                        value={Array.isArray(editing.draftValue) ? undefined : editing.draftValue}
                        onChange={handleDraftChange}
                        onKeyDown={handleEditKeyDown}
                        dataTypeCategory={valueCategoryFor(condition.operator)}
                        valueCodec={valueCodec}
                        valueTag={valueTagFor?.(condition, 'value')}
                        onValidityChange={setCodecEditError}
                        className="w-24"
                        autoFocus
                      />
                    )}
                    {/* Confirm button */}
                    <Button
                      variant="ghost"
                      size="sm"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={handleConfirmEdit}
                      disabled={!!codecEditError}
                      className="h-5 w-5 p-0 hover:bg-green-500/20 text-green-600"
                      title={codecEditError ?? 'Apply (Enter)'}
                      aria-label="Apply value"
                    >
                      <Check className="h-3 w-3" />
                    </Button>
                    {/* Cancel button */}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleCancelEdit}
                      className="h-5 w-5 p-0 hover:bg-destructive/20"
                      title="Cancel (Escape)"
                      aria-label="Cancel edit"
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </>
                ) : condition.operator === 'enum' &&
                  !enumSlot.available &&
                  Array.isArray(condition.value) ? (
                  // Enum array with no available picker: render read-only.
                  // Text-editing it would silently corrupt it to the scalar
                  // String(['A','B']) === "A,B". User can still remove the
                  // whole condition via the existing remove affordance.
                  <span className="text-xs px-2 py-0.5 text-muted-foreground">
                    {enumArraySummary(condition.value)}
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={() => handleStartEdit(index, 'primary')}
                    className="text-xs bg-background px-2 py-0.5 rounded border border-input min-w-[60px] text-left hover:border-primary hover:bg-accent cursor-text transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
                    title="Click to edit"
                  >
                    {displayFilterValue(condition.value, valueTagFor?.(condition, 'value')) || <span className="text-muted-foreground italic">empty</span>}
                  </button>
                )
              )}

              {/* Secondary value (for BETWEEN) - editable on click */}
              {operatorRequiresSecondValue(condition.operator) && (
                <>
                  <span className="text-xs text-muted-foreground">AND</span>
                  {isEditingSecondary ? (
                    <>
                      <FilterValueInput
                        ref={inputRef}
                        value={Array.isArray(editing.draftValue) ? undefined : editing.draftValue}
                        onChange={handleDraftChange}
                        onKeyDown={handleEditKeyDown}
                        dataTypeCategory={valueCategoryFor(condition.operator)}
                        valueCodec={valueCodec}
                        valueTag={valueTagFor?.(condition, 'value2')}
                        onValidityChange={setCodecEditError}
                        className="w-24"
                        autoFocus
                      />
                      {/* Confirm button */}
                      <Button
                        variant="ghost"
                        size="sm"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={handleConfirmEdit}
                        disabled={!!codecEditError}
                        className="h-5 w-5 p-0 hover:bg-green-500/20 text-green-600"
                        title={codecEditError ?? 'Apply (Enter)'}
                        aria-label="Apply second value"
                      >
                        <Check className="h-3 w-3" />
                      </Button>
                      {/* Cancel button */}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleCancelEdit}
                        className="h-5 w-5 p-0 hover:bg-destructive/20"
                        title="Cancel (Escape)"
                        aria-label="Cancel edit"
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </>
                  ) : (
                    <button
                      type="button"
                      onClick={() => handleStartEdit(index, 'secondary')}
                      className="text-xs bg-background px-2 py-0.5 rounded border border-input min-w-[60px] text-left hover:border-primary hover:bg-accent cursor-text transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
                      title="Click to edit"
                    >
                      {displayFilterValue(condition.value2, valueTagFor?.(condition, 'value2')) || <span className="text-muted-foreground italic">empty</span>}
                    </button>
                  )}
                </>
              )}

              {/* Remove condition button - hide when editing */}
              {!isEditing && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleRemoveCondition(index)}
                  className="h-5 w-5 p-0 hover:bg-destructive/20"
                  title="Remove condition"
                  aria-label={`Remove ${getOperatorLabel(condition.operator)} condition`}
                >
                  <X className="h-3 w-3" />
                </Button>
              )}
            </div>
          );
        })}

        {/* Draft OR condition */}
        {draftOrCondition && (
          <div className="flex items-center gap-1">
            <span className="text-xs text-muted-foreground px-1">OR</span>

            {/* Operator select */}
            <Select
              value={draftOrCondition.operator}
              onValueChange={(value: FilterOperator) => handleDraftOrOperatorChange(value)}
            >
              <SelectTrigger className="h-6 w-auto min-w-[70px] px-2 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent portalled={false}>
                {availableOperators.map((op) => (
                  <SelectItem key={op} value={op} className="text-xs">
                    {getOperatorLabel(op)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Value input */}
            {operatorRequiresValue(draftOrCondition.operator as FilterOperator) && (
              operatorUsesFkPicker(draftOrCondition.operator as FilterOperator) ? (
                <FkLookupValueButton
                  connectionId={connectionId!}
                  database={database!}
                  sourceColumn={filterGroup.field}
                  sourceSchema={tableSchema}
                  sourceTable={tableName}
                  sourceColumnDataType={column?.dataType ?? 'unknown'}
                  referencedSchema={column?.referencedSchema ?? ''}
                  referencedTable={column?.referencedTable ?? ''}
                  referencedColumn={column?.referencedColumn ?? ''}
                  referencesRealForeignKey={hasRealForeignKeyTarget(column)}
                  targetFilter={targetFilter}
                  value={Array.isArray(draftOrCondition.value) ? undefined : draftOrCondition.value}
                  engine={jsonContext?.engine}
                  onChange={(v, resolvedSourcePath) => {
                    // Picker selection is the user's confirmation — commit the
                    // OR condition immediately so they don't click Apply twice.
                    const sourcePath =
                      resolvedSourcePath ??
                      findLooseSourcePath(jsonContext, filterGroup.field);
                    const newCondition: FilterCondition = {
                      operator: draftOrCondition.operator as FilterCondition['operator'],
                      value: v,
                      ...(sourcePath ? { sourcePath } : {}),
                    };
                    onUpdate({
                      ...filterGroup,
                      conditions: [...filterGroup.conditions, newCondition],
                    });
                    setDraftOrCondition(null);
                  }}
                />
              ) : draftOrCondition.operator === 'enum' && enumSlot.available ? (
                <EnumValuePicker
                  {...enumSlot.context}
                  operator={Array.isArray(draftOrCondition.value) ? 'in' : 'eq'}
                  value={draftOrCondition.value}
                  onChange={(next) => {
                    // Picker selection commits the OR condition directly.
                    const newCondition: FilterCondition = {
                      operator: 'enum',
                      value: next,
                    };
                    onUpdate({
                      ...filterGroup,
                      conditions: [...filterGroup.conditions, newCondition],
                    });
                    setDraftOrCondition(null);
                    track('feature:enum_filter_apply', {
                      engine: enumSlot.context.engine,
                      operator: 'enum',
                      source: Array.isArray(next) ? 'multi' : 'pick',
                      ...(Array.isArray(next) ? { count: next.length } : {}),
                    });
                  }}
                  ariaLabel={`Pick OR value for ${filterGroup.field}`}
                />
              ) : operatorTakesValueList(draftOrCondition.operator as FilterOperator) ? (
                <FilterValueListInput
                  ref={draftInputRef}
                  values={listValueOf(draftOrCondition.value as FilterCondition['value'])}
                  onChange={handleDraftOrValueChange}
                  onKeyDown={handleDraftOrKeyDown}
                  dataTypeCategory={valueCategoryFor(draftOrCondition.operator as FilterOperator)}
                  autoFocus
                />
              ) : (
                <FilterValueInput
                  ref={draftInputRef}
                  value={Array.isArray(draftOrCondition.value) ? undefined : draftOrCondition.value}
                  onChange={handleDraftOrValueChange}
                  onKeyDown={handleDraftOrKeyDown}
                  dataTypeCategory={valueCategoryFor(draftOrCondition.operator as FilterOperator)}
                  valueCodec={valueCodec}
                  valueTag={valueTagFor?.(undefined, 'value')}
                  onValidityChange={setCodecDraftOrError}
                  className="w-24"
                  autoFocus
                />
              )
            )}

            {/* Confirm button */}
            <Button
              variant="ghost"
              size="sm"
              onMouseDown={(e) => e.preventDefault()}
              onClick={handleConfirmDraftOr}
              disabled={!draftOrHasValidValue || !!codecDraftOrError}
              className="h-5 w-5 p-0 hover:bg-green-500/20 text-green-600"
              title="Apply (Enter)"
              aria-label="Apply OR condition"
            >
              <Check className="h-3 w-3" />
            </Button>

            {/* Cancel button */}
            <Button
              variant="ghost"
              size="sm"
              onClick={handleCancelDraftOr}
              className="h-5 w-5 p-0 hover:bg-destructive/20"
              title="Cancel (Escape)"
              aria-label="Cancel OR condition"
            >
              <X className="h-3 w-3" />
            </Button>
          </div>
        )}
      </div>

      {/* Add OR condition button - hide when editing or draft OR exists */}
      {!isEditing && (
        <Button
          variant="ghost"
          size="sm"
          onClick={handleAddOrCondition}
          className="h-5 px-1 text-xs"
          title="Add OR condition"
        >
          <Plus className="h-3 w-3 mr-0.5" aria-hidden="true" />
          OR
        </Button>
      )}

      {pendingDeclaration && column && jsonContext && (
        <DeclareJsonColumnDialog
          open
          schema={jsonContext.schema}
          table={jsonContext.table}
          column={column.name}
          dataType={column.dataType}
          engineLabel={getEngineLabel(jsonContext.engine)}
          errorMessage={declarationError}
          unsupportedReason={declareOnTextUnsupportedReason(jsonContext)}
          sampleRows={declareSample}
          onConfirm={handleConfirmDeclaration}
          onCancel={handleCancelDeclaration}
        />
      )}
    </div>
  );
}

type DraftFilterChipProps = {
  filterGroup: FilterGroup;
  column: TableExplorerColumn | undefined;
  onUpdate: (updatedGroup: FilterGroup) => void;
  onCommit: (draftOverride?: FilterGroup) => void;
  onCancel: () => void;
  /** Connection id — required for the FK lookup picker (operator='lookup'). */
  connectionId?: string;
  /** Database name — required for the FK lookup picker. */
  database?: string;
  /** Source schema for loose-FK target picker context. */
  tableSchema?: string;
  /** Source table for loose-FK target picker context. */
  tableName?: string;
  /** Increments when the parent (FilterWizard) wants to draw attention to this
   * draft — fires the pulse animation + the "confirm or cancel first" tooltip. */
  pulseToken?: number;
  /** Engine + metaInfo context for the `json` operator. */
  jsonContext?: JsonOperatorContext;
  /**
   * Currently loaded grid rows — used to source a sample value for the
   * Declare-as-JSON confirmation preview. Optional; when omitted the dialog
   * simply renders without a preview block.
   */
  sampleRows?: ReadonlyArray<Record<string, unknown>>;
  /**
   * Applied Table Explorer filters (minus this draft's own group, which is
   * never in the applied set) to scope the JSON Peek popover's scan to —
   * filter-aware JSON peek (Path A). Passed down by FilterWizard via
   * `scanFiltersForChip`.
   */
  scanFilters?: FilterGroup[];
  valueCodec?: TableExplorerCellValueCodec;
  /**
   * Host value codecs only (Azure Table, S4 review D, D3): the tag of ONE operand — `value` or `value2` of this
   * condition (`undefined` = a new condition) — so each input parses with its own operand's type.
   */
  valueTagFor?: (condition: FilterCondition | undefined, operand: 'value' | 'value2') => string | undefined;
  /**
   * Value-input placeholder by the value's data-type category. Omitted →
   * the shared "Filter…" default (Table Explorer). The Visual Query Editor
   * passes "Number" / "Text" / "Value".
   */
  valuePlaceholder?: (category: DataTypeCategory) => string | undefined;
  /**
   * `'primary'` — the apply tick is muted grey while there is no value and a
   * filled primary button once there is one, so apply is the obvious next
   * click (Visual Query Editor). `'default'` keeps the green ghost tick.
   */
  applyStyle?: 'default' | 'primary';
};

/**
 * DraftFilterChip for filters being edited (not yet committed)
 * Shows editable inputs and Apply/Cancel buttons
 */
/**
 * Focus sits somewhere the draft's first-focus timer must not take it from:
 * inside the chip (other than its own value input, which the timer still
 * selects) or inside a dialog that is not the chip's own ancestor (a picker the
 * chip opened, such as the lookup row picker).
 */
function focusMovedOnItsOwn(root: HTMLElement | null, valueInput: HTMLElement | null): boolean {
  const active = document.activeElement;
  if (!active || active === document.body || active === valueInput) return false;
  if (root?.contains(active)) return true;
  const dialog = active.closest('[role="dialog"]');
  return dialog !== null && !(root && dialog.contains(root));
}

export function DraftFilterChip({
  filterGroup,
  column,
  onUpdate,
  onCommit,
  onCancel,
  connectionId,
  database,
  tableSchema,
  tableName,
  pulseToken,
  jsonContext,
  sampleRows,
  scanFilters,
  valueCodec,
  valueTagFor,
  valuePlaceholder,
  applyStyle = 'default',
}: DraftFilterChipProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const applyButtonRef = useRef<HTMLButtonElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const lastPulseRef = useRef<number | undefined>(pulseToken);
  const [pulseTooltipOpen, setPulseTooltipOpen] = useState(false);
  const [isPulsing, setIsPulsing] = useState(false);
  const [pendingDeclaration, setPendingDeclaration] = useState<{ pending: true } | null>(null);
  const [declarationError, setDeclarationError] = useState<string | null>(null);
  const invalidateMetaInfo = useInvalidateMetaInfo();
  const declareSample = useDeclareJsonSample({
    rows: sampleRows,
    column: column?.name,
    connectionId,
    database,
    schema: jsonContext?.schema,
    table: jsonContext?.table,
    engine: jsonContext?.engine,
    filters: scanFilters,
    open: !!pendingDeclaration,
  });
  const { track } = useAnalytics();

  // Replay pulse + tooltip whenever the parent bumps the token. State-driven
  // (Tailwind `animate-pulse` + amber ring) so it stands out against the
  // chip's own blue border — a boxShadow pulse in the same blue family was
  // effectively invisible.
  useEffect(() => {
    if (pulseToken === undefined) return;
    if (pulseToken === lastPulseRef.current) return;
    lastPulseRef.current = pulseToken;

    setIsPulsing(true);
    setPulseTooltipOpen(true);
    const pulseEnd = setTimeout(() => setIsPulsing(false), 1200);
    const tooltipEnd = setTimeout(() => setPulseTooltipOpen(false), 2500);
    return () => {
      clearTimeout(pulseEnd);
      clearTimeout(tooltipEnd);
    };
  }, [pulseToken]);

  const dataTypeCategory: DataTypeCategory = column
    ? categorizeDataType(column.dataType)
    : 'unknown';
  // Synthetic expression fields (the VQE aggregate templates pass
  // `tableName=''`) have no backing table for a loose-FK declaration — drop
  // the dead-end `lookup` entry. No-op for Table Explorer, which always has
  // a real table name (and for callers omitting `tableName` entirely).
  const availableOperators = excludeLookupWithoutBackingTable(
    getOperatorsForColumnWithMetaInfo(column, jsonContext),
    tableName,
  );
  // The picker handles the "no declared target yet" case (loose-FK target
  // selection UI), so we just need a connection + database here.
  const canRenderLookupPicker = !!connectionId && !!database;

  const valueCategoryFor = (operator: FilterOperator): DataTypeCategory => {
    if (operatorIsArrayLength(operator)) return 'number';
    if (operatorIsArrayElement(operator) && column?.dataType) {
      return categorizeDataType(arrayElementDataType(column.dataType));
    }
    return dataTypeCategory;
  };

  // Only `lookup` uses the FK picker. `array_contains` (and other array ops)
  // use the regular value-input UX — free-text or category-typed input. The
  // user can switch to `lookup` from the dropdown to get the picker version,
  // which the provider lowers to `array_contains` SQL semantics on array fields.
  const operatorUsesFkPicker = (operator: FilterOperator): boolean => {
    if (!canRenderLookupPicker) return false;
    return operator === 'lookup';
  };

  const targetFilter = useColumnTargetFilter(
    connectionId,
    database,
    tableSchema,
    tableName,
    filterGroup.field,
    column,
  );

  // EnumValuePicker — rendered when the `enum` operator is selected.
  const { data: metaInfoForEnum } = useMetaInfo(
    connectionId ?? null,
    database ?? null,
  );
  const enumSlot = useEnumPickerSlot({
    connectionId,
    database,
    schema: tableSchema,
    table: tableName,
    engine: jsonContext?.engine,
    column,
    metaInfo: metaInfoForEnum,
  });

  // Focus the value input when the draft chip appears, or the Apply button when
  // the operator has no value input (e.g. IS NULL pre-filled from cell context menu)
  // — unless focus already moved on its own by the time the timer fires: into
  // this chip (the JSON path popover opens with its path field focused, or the
  // user clicked a field) or into a dialog it opened. Pulling it away then sent
  // typing to Apply, and an Escape meant for the path popover cancelled the draft.
  useEffect(() => {
    const openedWith = inputRef.current?.value;
    const timeout = setTimeout(() => {
      if (focusMovedOnItsOwn(rootRef.current, inputRef.current)) return;
      // Typing already began in the value input: selecting it now would let
      // the next key replace what was typed.
      if (inputRef.current && inputRef.current.value !== openedWith) return;
      if (inputRef.current) {
        inputRef.current.focus();
        inputRef.current.select();
      } else {
        applyButtonRef.current?.focus();
      }
    }, 50);
    return () => clearTimeout(timeout);
  }, []);

  // When the draft is opened on a column that auto-defaults to `json` because
  // its name ends in `_json` (placement = 'first' on a text column), the
  // column is still `json-eligible` rather than `declared-json`. Surface the
  // declaration prompt immediately so the user doesn't have to switch the
  // operator off and back on to trigger it.
  useEffect(() => {
    const initial = filterGroup.conditions[0];
    if (!initial || initial.operator !== 'json') return;
    if (initial.jsonDeclaredOnText) return;
    if (!column || !jsonContext) return;
    const category = categorizeJsonColumn(
      {
        engine: jsonContext.engine,
        schema: jsonContext.schema,
        table: jsonContext.table,
        name: column.name,
        dataType: column.dataType,
      },
      jsonContext.metaInfo,
    );
    if (category === 'json-eligible') {
      setPendingDeclaration({ pending: true });
    }
    // Mount-only — declaration is a one-shot prompt tied to the draft's
    // initial state. Subsequent operator picks go through `handleOperatorChange`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update a specific condition
  const handleConditionChange = useCallback(
    (index: number, updates: Partial<FilterCondition>) => {
      const newConditions = [...filterGroup.conditions];
      newConditions[index] = { ...newConditions[index], ...updates };
      onUpdate({ ...filterGroup, conditions: newConditions });
    },
    [filterGroup, onUpdate]
  );

  // Handle operator change - auto-commit for IS NULL / IS NOT NULL
  const handleOperatorChange = useCallback(
    (index: number, operator: FilterOperator) => {
      // Single categorize() pass for the `json`-related branches below.
      const jsonCategory =
        operator === 'json' && column && jsonContext
          ? categorizeJsonColumn(
              {
                engine: jsonContext.engine,
                schema: jsonContext.schema,
                table: jsonContext.table,
                name: column.name,
                dataType: column.dataType,
              },
              jsonContext.metaInfo,
            )
          : null;

      // `json` on a json-eligible (long-text, undeclared) column → confirm
      // declaration before applying. The dialog confirm handler completes
      // the operator change.
      if (jsonCategory === 'json-eligible') {
        setPendingDeclaration({ pending: true });
        return;
      }

      const conditionUpdates: Partial<FilterCondition> = { operator };
      if (!operatorRequiresValue(operator)) {
        conditionUpdates.value = undefined;
        conditionUpdates.value2 = undefined;
      } else {
        // A typed value carries over — as a one-value list into "in list", as
        // a list's first value out of it.
        conditionUpdates.value = valueForOperator(filterGroup.conditions[index]?.value, operator);
      }
      // sourcePath is `lookup`-only — clear when switching operators so the
      // SQL builder doesn't see stale JSON-extract state.
      if (operator !== 'lookup') {
        conditionUpdates.sourcePath = undefined;
      }
      if (jsonCategory) {
        conditionUpdates.jsonSubOperator =
          (filterGroup.conditions[index]?.jsonSubOperator) ?? 'has_property';
        conditionUpdates.jsonDeclaredOnText =
          jsonCategory === 'declared-json' &&
          !isNativeJsonColumn({
            engine: jsonContext!.engine,
            schema: jsonContext!.schema,
            table: jsonContext!.table,
            name: column!.name,
            dataType: column!.dataType,
          });
      }

      const newConditions = [...filterGroup.conditions];
      newConditions[index] = { ...newConditions[index], ...conditionUpdates };
      const updatedGroup: FilterGroup = { ...filterGroup, conditions: newConditions };
      onUpdate(updatedGroup);

      // Auto-commit for operators that don't require a value (but NOT for
      // `json`, which still needs a sub-op + path before the user clicks Apply).
      if (!operatorRequiresValue(operator) && operator !== 'json') {
        setTimeout(() => onCommit(updatedGroup), 0);
      }
    },
    [column, filterGroup, jsonContext, onCommit, onUpdate]
  );

  // Persist the declaration to MetaInfo on confirm so the action survives
  // draft cancellation (matches the committed-chip flow). The user explicitly
  // opted in via the dialog — surfacing a sticky declaration is the correct
  // mental model; if they regret it they can remove it from Manage Meta-Info.
  const handleConfirmDeclaration = useCallback(async () => {
    if (!column || !jsonContext || !connectionId || !database) {
      setPendingDeclaration(null);
      setDeclarationError(null);
      return;
    }
    setDeclarationError(null);
    try {
      await addJsonColumn(connectionId, database, {
        id: newDeclarationId(),
        schema: jsonContext.schema,
        table: jsonContext.table,
        column: column.name,
        createdAt: new Date().toISOString(),
      });
      track('feature:json_declare', {
        engine: jsonContext.engine,
        source: 'filter-confirm',
      });
      await invalidateMetaInfo(connectionId, database);
    } catch (err) {
      setDeclarationError(friendlyDeclareError(err));
      return;
    }
    const newConditions = [...filterGroup.conditions];
    newConditions[0] = {
      ...newConditions[0],
      operator: 'json',
      jsonSubOperator: 'has_property',
      jsonDeclaredOnText: true,
      value: undefined,
      value2: undefined,
    };
    onUpdate({ ...filterGroup, conditions: newConditions });
    setPendingDeclaration(null);
  }, [column, connectionId, database, filterGroup, invalidateMetaInfo, jsonContext, onUpdate, track]);

  const handleCancelDeclaration = useCallback(() => {
    setPendingDeclaration(null);
    setDeclarationError(null);
  }, []);

  // Declaration is already persisted at confirm-time (see
  // `handleConfirmDeclaration`), so Apply just commits the filter.
  // A host codec input reports an invalid draft (S4 review D, D3); an invalid draft is never applied.
  const [codecErrors, setCodecErrors] = useState<{ value?: string; value2?: string }>({});
  const codecError = codecErrors.value ?? codecErrors.value2;
  const setValueCodecError = useCallback((error: string | undefined) => setCodecErrors((current) => current.value === error ? current : { ...current, value: error }), []);
  const setValue2CodecError = useCallback((error: string | undefined) => setCodecErrors((current) => current.value2 === error ? current : { ...current, value2: error }), []);
  const handleApply = useCallback(
    (override?: FilterGroup) => {
      if (codecError && !override) return;
      trackListFilters(track, jsonContext?.engine, (override ?? filterGroup).conditions);
      onCommit(override);
    },
    [codecError, filterGroup, jsonContext?.engine, onCommit, track],
  );

  // Handle key press in value input
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleApply();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        onCancel();
      }
    },
    [handleApply, onCancel]
  );

  // codex P2 #2 — the root's `escapeClaimProps` marker (below) claims Escape
  // for the whole draft-chip subtree unconditionally, but only the value
  // input(s) / JSON cluster ever had a keydown handler wired to `onCancel`.
  // Tabbing to the operator select or the Apply/Cancel buttons and pressing
  // Escape there did nothing — claimed but unhandled. Escape-only (not
  // Enter, which `handleKeyDown` above already scopes to inputs so it never
  // double-fires against the operator select's own Enter handling).
  const handleDraftRootKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      e.stopPropagation();
      onCancel();
    },
    [onCancel],
  );

  // Check if the filter has a valid value (a list needs at least one value)
  const hasValidValue = filterGroup.conditions.every((c) => {
    if (!operatorRequiresValue(c.operator)) return true;
    if (operatorTakesValueList(c.operator)) return hasFilterValue(c.value);
    if (c.value === '' || c.value === null || c.value === undefined) return false;
    if (operatorRequiresSecondValue(c.operator)) {
      if (c.value2 === '' || c.value2 === null || c.value2 === undefined) return false;
    }
    return true;
  });

  const condition = filterGroup.conditions[0];

  return (
    <Tooltip open={pulseTooltipOpen}>
      <TooltipTrigger asChild>
        <div
          ref={rootRef}
          data-testid={`filter-draft-chip-${normalizeColumnName(filterGroup.field)}`}
          data-pulsing={isPulsing ? 'true' : undefined}
          className={`flex items-center gap-1 rounded-md border-2 px-2 py-1 transition-colors ${
            isPulsing
              ? 'border-amber-500 bg-amber-100 ring-4 ring-amber-400/60 animate-pulse motion-reduce:animate-none dark:bg-amber-900/40'
              : 'border-blue-500 bg-blue-50 dark:bg-blue-950/30'
          }`}
          // The draft chip is ALWAYS an in-progress edit for as long as it's
          // mounted (no separate idle/editing toggle like `FilterChip`), so
          // the whole container claims Escape unconditionally. This lets an
          // ancestor `CompactVqePopover` (VQE's `VqeFilterChipBuilder` reuses
          // this component for its structured chip editor) claim Escape for
          // any descendant input via `onEscapeKeyDown` + `isEscapeClaimTarget`
          // (F1 — Escape-in-popover seam consolidation); inert everywhere
          // else (Table Explorer / Data Profile host no such ancestor check).
          {...escapeClaimProps}
          onKeyDown={handleDraftRootKeyDown}
        >
          {/* Field name */}
          <span translate="no" className="text-xs font-medium text-foreground shrink-0">
            {filterGroup.field}
          </span>

          {/* Single condition (draft only supports one condition initially) */}
          <div className="flex items-center gap-1">
            {/* Operator select */}
            <Select
              value={condition.operator}
              onValueChange={(value: FilterOperator) => handleOperatorChange(0, value)}
            >
              <SelectTrigger className="h-6 w-auto min-w-[70px] px-2 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent portalled={false}>
                {availableOperators.map((op) => (
                  <SelectItem key={op} value={op} className="text-xs">
                    {getOperatorLabel(op)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* JSON operator field cluster */}
            {condition.operator === 'json' && (
              <span
                className="inline-flex flex-wrap items-center gap-1 @max-3xl:max-w-md"
                onKeyDown={handleKeyDown}
              >
                <JsonFilterFields
                  condition={condition}
                  onChange={(updates) => handleConditionChange(0, updates)}
                  autoFocusPath
                  applyButtonRef={applyButtonRef}
                  engine={jsonContext?.engine}
                  peekContext={
                    column && jsonContext && connectionId && database
                      ? {
                          connectionId,
                          database,
                          schema: jsonContext.schema,
                          table: jsonContext.table,
                          column: column.name,
                          engine: jsonContext.engine,
                          declaredOnText: !!condition.jsonDeclaredOnText,
                          dataType: column.dataType,
                          metaInfo: jsonContext.metaInfo ?? null,
                          scanFilters,
                        }
                      : undefined
                  }
                />
              </span>
            )}

            {/* Value input(s) */}
            {operatorRequiresValue(condition.operator) && (
              <>
                {operatorUsesFkPicker(condition.operator) ? (
                  <FkLookupValueButton
                    connectionId={connectionId!}
                    database={database!}
                    sourceColumn={filterGroup.field}
                    sourceSchema={tableSchema}
                    sourceTable={tableName}
                    sourceColumnDataType={column?.dataType ?? 'unknown'}
                    referencedSchema={column?.referencedSchema ?? ''}
                    referencedTable={column?.referencedTable ?? ''}
                    referencedColumn={column?.referencedColumn ?? ''}
                    referencesRealForeignKey={hasRealForeignKeyTarget(column)}
                    targetFilter={targetFilter}
                    value={condition.value as string | number | bigint | boolean | Date | null | undefined}
                    engine={jsonContext?.engine}
                    sourcePath={condition.sourcePath}
                    onChange={(v, resolvedSourcePath) => {
                      // Picking a row in the picker is the user's confirmation —
                      // commit the draft directly so they don't have to click Apply.
                      const sourcePath =
                        resolvedSourcePath ??
                        condition.sourcePath ??
                        findLooseSourcePath(jsonContext, filterGroup.field);
                      const updated: FilterGroup = {
                        ...filterGroup,
                        conditions: [{ ...condition, value: v, ...(sourcePath ? { sourcePath } : {}) }],
                      };
                      onUpdate(updated);
                      setTimeout(() => onCommit(updated), 0);
                    }}
                    onClearFilter={onCancel}
                  />
                ) : condition.operator === 'enum' && enumSlot.available ? (
                  <EnumValuePicker
                    {...enumSlot.context}
                    operator={Array.isArray(condition.value) ? 'in' : 'eq'}
                    value={condition.value}
                    onChange={(next) => {
                      // Picker selection commits the draft directly — same UX
                      // as the FK picker above (one click instead of pick + Apply).
                      const updated: FilterGroup = {
                        ...filterGroup,
                        conditions: [{ ...condition, value: next }],
                      };
                      onUpdate(updated);
                      setTimeout(() => onCommit(updated), 0);
                      track('feature:enum_filter_apply', {
                        engine: enumSlot.context.engine,
                        operator: 'enum',
                        source: Array.isArray(next) ? 'multi' : 'pick',
                        ...(Array.isArray(next) ? { count: next.length } : {}),
                      });
                    }}
                    ariaLabel={`Pick value for ${filterGroup.field}`}
                  />
                ) : operatorTakesValueList(condition.operator) ? (
                  <FilterValueListInput
                    ref={inputRef}
                    values={listValueOf(condition.value)}
                    onChange={(values) => handleConditionChange(0, { value: values })}
                    onKeyDown={handleKeyDown}
                    dataTypeCategory={valueCategoryFor(condition.operator)}
                    autoFocus
                  />
                ) : condition.operator === 'enum' &&
                  !enumSlot.available &&
                  Array.isArray(condition.value) ? (
                  // Enum array with no available picker: render read-only.
                  // Text-editing it would silently corrupt it to the scalar
                  // String(['A','B']) === "A,B". User can still cancel/remove
                  // the draft via the existing affordances.
                  <span className="text-xs px-2 py-0.5 text-muted-foreground">
                    {enumArraySummary(condition.value)}
                  </span>
                ) : (
                  <FilterValueInput
                    ref={inputRef}
                    value={condition.value as string | number | boolean | Date | null | undefined}
                    onChange={(value) => handleConditionChange(0, { value })}
                    onKeyDown={handleKeyDown}
                    dataTypeCategory={valueCategoryFor(condition.operator)}
                    valueCodec={valueCodec}
                    valueTag={valueTagFor?.(condition, 'value')}
                    onValidityChange={setValueCodecError}
                    placeholder={valuePlaceholder?.(valueCategoryFor(condition.operator))}
                    className="w-24"
                    autoFocus
                  />
                )}
                {operatorRequiresSecondValue(condition.operator) && (
                  <>
                    <span className="text-xs text-muted-foreground">AND</span>
                    <FilterValueInput
                      value={condition.value2}
                      onChange={(value) =>
                        handleConditionChange(0, {
                          value2: value as string | number | bigint | Date | undefined,
                        })
                      }
                      onKeyDown={handleKeyDown}
                      dataTypeCategory={valueCategoryFor(condition.operator)}
                      valueCodec={valueCodec}
                      valueTag={valueTagFor?.(condition, 'value2')}
                      onValidityChange={setValue2CodecError}
                      placeholder={valuePlaceholder?.(valueCategoryFor(condition.operator))}
                      className="w-24"
                    />
                  </>
                )}
              </>
            )}
          </div>

          {/* Apply button */}
          <Button
            ref={applyButtonRef}
            variant={applyStyle === 'primary' && hasValidValue && !codecError ? 'default' : 'ghost'}
            size="sm"
            onClick={() => handleApply()}
            disabled={!hasValidValue || !!codecError}
            className={
              applyStyle === 'primary'
                ? hasValidValue && !codecError
                  ? 'h-5 w-5 p-0'
                  : 'h-5 w-5 p-0 text-muted-foreground'
                : 'h-5 w-5 p-0 hover:bg-green-500/20 text-green-600'
            }
            data-apply-style={applyStyle}
            title="Apply filter (Enter)"
            aria-label="Apply filter"
          >
            <Check className="h-3 w-3" />
          </Button>

          {/* Cancel button */}
          <Button
            variant="ghost"
            size="sm"
            onClick={onCancel}
            className="h-5 w-5 p-0 hover:bg-destructive/20"
            title="Cancel (Escape)"
            aria-label="Cancel filter"
          >
            <X className="h-3 w-3" />
          </Button>
        </div>
      </TooltipTrigger>
      <TooltipContent side="bottom" data-testid="filter-draft-pulse-tooltip">
        Apply or cancel this filter before adding another
      </TooltipContent>
      {pendingDeclaration && column && jsonContext && (
        <DeclareJsonColumnDialog
          open
          schema={jsonContext.schema}
          table={jsonContext.table}
          column={column.name}
          dataType={column.dataType}
          engineLabel={getEngineLabel(jsonContext.engine)}
          errorMessage={declarationError}
          unsupportedReason={declareOnTextUnsupportedReason(jsonContext)}
          sampleRows={declareSample}
          onConfirm={handleConfirmDeclaration}
          onCancel={handleCancelDeclaration}
        />
      )}
    </Tooltip>
  );
}

/**
 * Display-only version of FilterChip for compact view
 */
export function FilterChipCompact({
  filterGroup,
  onRemove,
}: {
  filterGroup: FilterGroup;
  onRemove: () => void;
}) {
  const conditionText = filterGroup.conditions
    .map((c) => {
      const opLabel = getOperatorLabel(c.operator);
      if (!operatorRequiresValue(c.operator)) {
        return opLabel;
      }
      if (operatorRequiresSecondValue(c.operator)) {
        return `${opLabel} ${formatFilterValue(c.value)} AND ${formatFilterValue(c.value2)}`;
      }
      if (c.operator === 'enum' && Array.isArray(c.value)) {
        return `in ${c.value.length} ${c.value.length === 1 ? 'value' : 'values'}`;
      }
      return `${opLabel} ${formatFilterValue(c.value)}`;
    })
    .join(' OR ');

  return (
    <div className="inline-flex items-center gap-1 rounded-full border border-border bg-muted/50 px-2 py-0.5 text-xs">
      <span translate="no" className="font-medium">{filterGroup.field}</span>
      <span className="text-muted-foreground">{conditionText}</span>
      <Button
        variant="ghost"
        size="sm"
        onClick={onRemove}
        className="h-4 w-4 p-0 hover:bg-destructive/20 ml-1"
        title="Remove filter"
        aria-label={`Remove ${filterGroup.field} filter`}
      >
        <X className="h-3 w-3" />
      </Button>
    </div>
  );
}
